import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ShieldCheck, Sparkles, Clock, Zap, ThumbsUp, CheckCircle2, Droplets,
  Star, Phone, ArrowRight, Loader2, BadgeCheck, MessageCircle,
} from "lucide-react";
import MarketingLayout from "../components/MarketingLayout";
import WhatsAppFab from "../components/WhatsAppFab";
import { api } from "../lib/api";
import { useCompany, fmtNipc, Brand, brandShort } from "../context/CompanyContext";

/**
 * Public homepage — dark-luxe palette (deep green + white + gold) per the
 * iter43 user request. WhatsApp removed everywhere; the only quick
 * contact action besides the form is "Ligar agora".
 *
 * Sections: Hero · Benefits · Testimonials · Trust · Contact + Promo · Sticky mobile CTA.
 */

const TRUST_IMG = "/assets/team-luxstay.png";

// Background hero video — professional cleaning footage (CC0 / Pexels
// #6197078 "Team of female cleaners working"). Landscape 16:9, perfect
// for a Hero watermark. `_MOBILE` is a smaller, low-bandwidth encoding;
// the `<video>` element falls back to desktop if not supported.
const HERO_VIDEO_DESKTOP = "https://videos.pexels.com/video-files/6197078/6197078-hd_1920_1080_25fps.mp4";
const HERO_VIDEO_MOBILE = "https://videos.pexels.com/video-files/6197078/6197078-sd_640_360_25fps.mp4";
// Poster image shown instantly while the video buffers — keeps the hero
// readable even on the very first paint or on slow connections.
const HERO_VIDEO_POSTER = "https://images.pexels.com/videos/6197078/pexels-photo-6197078.jpeg?auto=compress&cs=tinysrgb&w=1600";

// Replace with the real business phone when known.
const PHONE_LINK = "tel:+351936942709";

const REQUIRED_PHRASES = [
  "Serviço impecável, recomendo totalmente",
  "Superou as minhas expectativas",
];

export default function Home() {
  const [reviews, setReviews] = useState([]);
  // FAB do WhatsApp aparece SÓ depois do splash terminar — evita ficar a piscar
  // por cima da splash. `sessionStorage` flag também garante que recargas
  // dentro da mesma sessão (sem splash) mostram o FAB de imediato.
  const [showFab, setShowFab] = useState(() => {
    try { return !!sessionStorage.getItem("luxstay-splash-v2"); }
    catch { return true; }
  });

  useEffect(() => {
    api.get("/reviews/random", { params: { n: 6 } })
      .then(({ data }) => setReviews(data.items || []))
      .catch(() => setReviews([]));
  }, []);

  useEffect(() => {
    function onSplashDone() { setShowFab(true); }
    window.addEventListener("luxstay:splash-done", onSplashDone);
    return () => window.removeEventListener("luxstay:splash-done", onSplashDone);
  }, []);

  const testimonials = [
    { name: "Sofia M.", role: "Cliente · Funchal", quote: REQUIRED_PHRASES[0], rating: 5 },
    { name: "André L.", role: "Cliente · Caniço", quote: REQUIRED_PHRASES[1], rating: 5 },
    ...reviews.map(r => ({
      name: r.name || "Cliente",
      role: r.role || "Cliente verificado",
      quote: r.text || r.message || "Recomendo vivamente.",
      rating: r.rating || 5,
    })),
  ].slice(0, 6);

  return (
    <MarketingLayout>
      <Hero />
      <PromoBanner />
      <HeroCredentials />
      <Benefits />
      <Pricing />
      <Testimonials items={testimonials} />
      <TrustStrip />
      <ContactPromo />
      <StickyMobileCTA />
      {showFab && <WhatsAppFab />}
    </MarketingLayout>
  );
}

/* ──────────────────────────────  Hero  ────────────────────────────── */
// Smooth scroll helper for in-page anchors. React Router v6's <Link to="#id">
// only updates location.hash without actually scrolling, so any CTA that
// targets a section on the same page must use a plain <a> + this handler.
function scrollToContacto(e) {
  e?.preventDefault?.();
  const el = document.getElementById("contacto");
  if (el) {
    el.scrollIntoView({ behavior: "smooth", block: "start" });
    // Update the URL so the anchor is shareable without triggering the
    // default jumpy browser behaviour.
    try { window.history.replaceState(null, "", "#contacto"); } catch { /* noop */ }
    // Focus the first form field so keyboard users can start typing
    setTimeout(() => {
      const first = el.querySelector("input, textarea");
      if (first) first.focus({ preventScroll: true });
    }, 450);
  }
}

function Hero() {
  return (
    <section className="relative overflow-hidden isolate" data-testid="hero-section">
      {/* Watermark video background — autoplay, muted, loop. The poster
          gives an instant first paint while the file buffers; on phones the
          smaller MP4 is preferred via `media="..."` source ordering.
          Hidden from screen readers — purely decorative. */}
      <div aria-hidden="true"
           className="absolute inset-0 -z-10 overflow-hidden bg-[#031411]">
        <video
          className="hero-bg-video w-full h-full object-cover"
          autoPlay muted loop playsInline
          preload="metadata"
          poster={HERO_VIDEO_POSTER}
          data-testid="hero-bg-video">
          <source src={HERO_VIDEO_MOBILE} type="video/mp4" media="(max-width: 768px)" />
          <source src={HERO_VIDEO_DESKTOP} type="video/mp4" />
        </video>
        {/* Dark overlay — keeps text legible over any frame of the loop. */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#041f1b]/95 via-[#041f1b]/80 to-[#041f1b]/55" />
        {/* Gold glow over the overlay (subtle accent) */}
        <div className="absolute -top-32 -right-32 w-[28rem] h-[28rem] rounded-full bg-[#d4af37]/15 blur-3xl pointer-events-none" />
      </div>

      <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-28 lg:py-36 text-center">
        <div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]"
              data-testid="hero-headline">
            <span className="text-[#fdfbf7]">Limpeza profissional que </span>
            <span className="text-[#d4af37] italic">impressiona</span>
            <span className="text-[#fdfbf7]"> desde a primeira visita</span>
          </h1>

          <p className="mt-5 text-lg sm:text-xl text-[#a0b2af] max-w-xl mx-auto"
             data-testid="hero-subheadline">
            Serviço rápido, confiável e com resultados consistentes.
          </p>

          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <a href="#contacto"
               onClick={(e) => scrollToContacto(e)}
               className="inline-flex justify-center items-center px-7 py-4 text-base sm:text-lg font-semibold rounded-sm uppercase tracking-widest text-[#031411] bg-[#d4af37] hover:bg-[#f5d76e] shadow-lg shadow-[#d4af37]/20 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#d4af37] focus:ring-offset-2 focus:ring-offset-[#041f1b]"
               data-testid="hero-cta-button">
              Pedir orçamento gratuito
              <ArrowRight size={18} className="ml-2" />
            </a>
            <a href={PHONE_LINK}
               className="inline-flex justify-center items-center gap-2 px-7 py-4 text-base font-semibold rounded-sm uppercase tracking-widest text-[#d4af37] border-2 border-[#d4af37]/50 hover:bg-[#d4af37]/10 transition-colors focus:outline-none focus:ring-2 focus:ring-[#d4af37]"
               data-testid="hero-phone-button">
              <Phone size={18} /> Ligar agora
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────  Promo banner  ─────────────────────────── */
/** Full-featured promo card right below the hero video — highlights the
 *  60-day onboarding offer with an expanded layout (headline, perks
 *  checklist, scarcity pill and CTA). The gold "breathing" border is a
 *  subtle pulsing box-shadow that draws attention without being tacky.
 *  Respects `prefers-reduced-motion`. */
function PromoBanner() {
  // Admin-configurable number of days for the welcome promo. Falls back
  // to 60 if the public settings endpoint is unreachable (never block the
  // marketing page on a failed request).
  const [promoDays, setPromoDays] = useState(30);
  const [promoEnabled, setPromoEnabled] = useState(true);
  useEffect(() => {
    let alive = true;
    api.get("/public/welcome-promo").then(({ data }) => {
      if (!alive) return;
      if (typeof data?.days === "number" && data.days > 0) setPromoDays(data.days);
      if (typeof data?.enabled === "boolean") setPromoEnabled(data.enabled);
    }).catch(() => { /* silent */ });
    return () => { alive = false; };
  }, []);
  if (!promoEnabled) return null;

  const perks = [
    "Equipa dedicada ao seu apartamento",
    "Resposta prioritária 24/7",
    "Sem fidelização — saia quando quiser",
    "Acesso completo à plataforma",
  ];
  return (
    <section id="promo"
             className="bg-[#041f1b] py-10 sm:py-14"
             data-testid="promo-banner-section">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="promo-glow relative rounded-2xl bg-[#062c27] border-2 border-[#d4af37] p-7 sm:p-10"
             data-testid="promo-banner-card">

          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#d4af37]/10 text-[#d4af37] text-xs sm:text-sm font-semibold border border-[#d4af37]/40"
                data-testid="promo-banner-pill">
            <Sparkles size={14} />
            Oferta exclusiva para novos clientes
          </span>

          <h2 className="mt-5 font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.05]"
              data-testid="promo-banner-headline">
            <span className="text-[#d4af37] italic">{promoDays} dias</span>{" "}
            <span className="text-[#fdfbf7]">de promoção especial</span>
          </h2>

          <p className="mt-4 text-base sm:text-lg text-[#a0b2af] max-w-2xl leading-relaxed"
             data-testid="promo-banner-description">
            Experimente o nosso serviço com condições vantajosas durante os primeiros {promoDays} dias.
          </p>

          <div className="mt-6">
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#d4af37]/10 text-[#d4af37] text-xs sm:text-sm font-semibold border border-[#d4af37]/40 tracking-widest uppercase"
                  data-testid="promo-banner-scarcity">
              <Zap size={14} />
              Vagas limitadas
            </span>
          </div>

          <ul className="mt-7 space-y-3" data-testid="promo-banner-perks">
            {perks.map(p => (
              <li key={p} className="flex items-start gap-3 text-sm sm:text-base text-[#fdfbf7]">
                <span className="inline-flex items-center justify-center w-6 h-6 rounded-full border border-[#d4af37]/50 text-[#d4af37] shrink-0 mt-0.5"
                      aria-hidden="true">
                  <CheckCircle2 size={14} />
                </span>
                <span>{p}</span>
              </li>
            ))}
          </ul>

          <Link to={`/registo?bonus=${promoDays}`}
                className="mt-8 w-full sm:w-auto inline-flex justify-center items-center gap-2 px-7 py-4 rounded-sm text-sm sm:text-base font-semibold uppercase tracking-widest text-[#031411] bg-[#d4af37] hover:bg-[#f5d76e] shadow-lg shadow-[#d4af37]/25 transition-colors focus:outline-none focus:ring-2 focus:ring-[#d4af37]/60 focus:ring-offset-2 focus:ring-offset-[#062c27]"
                data-testid="promo-banner-cta">
            Quero aproveitar
            <ArrowRight size={18} />
          </Link>
        </div>
      </div>

      {/* Pulsing gold "breathing light" along the card borders. The inset
          shadow creates the internal golden glow; the outer shadow spills
          the halo onto the dark-green background. */}
      <style>{`
        @keyframes promoGlow {
          0%, 100% {
            box-shadow:
              inset 0 0 24px 0 rgba(212, 175, 55, 0.20),
              0 0 24px 0 rgba(212, 175, 55, 0.22);
          }
          50% {
            box-shadow:
              inset 0 0 42px 2px rgba(212, 175, 55, 0.45),
              0 0 46px 6px rgba(212, 175, 55, 0.50);
          }
        }
        .promo-glow {
          animation: promoGlow 2.6s ease-in-out infinite;
        }
        @media (prefers-reduced-motion: reduce) {
          .promo-glow { animation: none; }
        }
      `}</style>
    </section>
  );
}

/* ─────────────────  Hero credentials (moved out of hero)  ─────────── */
/** Trust pills + 5★ Google + NIPC badge placed right after the promo so
 *  the hero stays visually focused on the video + headline + CTAs. */
function HeroCredentials() {
  const { company } = useCompany();
  return (
    <section className="bg-[#041f1b] py-10 sm:py-12" data-testid="hero-credentials-section">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <ul className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs sm:text-sm text-[#a0b2af]">
          <li className="inline-flex items-center gap-1.5"><CheckCircle2 size={14} className="text-[#d4af37]"/> Sem fidelização</li>
          <li className="inline-flex items-center gap-1.5"><CheckCircle2 size={14} className="text-[#d4af37]"/> Resposta em 24h</li>
          <li className="inline-flex items-center gap-1.5"><CheckCircle2 size={14} className="text-[#d4af37]"/> Equipa local · Madeira</li>
          <li className="inline-flex items-center gap-1.5" data-testid="hero-verified-badge">
            <BadgeCheck size={14} className="text-[#d4af37]"/> Equipa verificada · fardada
          </li>
        </ul>

        <div className="mt-6 flex flex-wrap items-center justify-center gap-3" data-testid="hero-credentials">
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#062c27] border border-[#d4af37]/35 text-xs text-[#fdfbf7]"
                data-testid="hero-google-rating">
            <span className="flex text-[#d4af37]" aria-hidden="true">
              {[...Array(5)].map((_, i) => <Star key={i} size={11} fill="currentColor" stroke="currentColor" />)}
            </span>
            <span className="font-semibold">5.0</span>
            <span className="text-[#a0b2af]">Google</span>
          </span>
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#062c27] border border-[#d4af37]/35 text-xs text-[#fdfbf7]"
                data-testid="hero-nipc-badge"
                title="Empresa registada em Portugal">
            <ShieldCheck size={12} className="text-[#d4af37]" />
            <span className="font-semibold">Empresa registada</span>
            {company.nipc && <span className="text-[#a0b2af]">· NIPC {fmtNipc(company.nipc)}</span>}
          </span>
        </div>
      </div>
    </section>
  );
}

/* ───────────────────────────  Benefits  ───────────────────────────── */
function Benefits() {
  const pillars = [
    { Icon: ShieldCheck, title: "Profissionais de confiança",
      body: "Equipa fixa, fardada e treinada. As mesmas caras nas suas limpezas." },
    { Icon: Sparkles, title: "Limpeza detalhada e consistente",
      body: "Checklist de 50+ pontos por T0/T1/T2/T3 — nada fica esquecido." },
    { Icon: Clock, title: "Flexibilidade de horários",
      body: "Manhãs, noites e fins-de-semana. Adaptamos-nos ao check-in/out." },
    { Icon: Zap, title: "Resposta rápida",
      body: "Pedido confirmado em 24h. Limpezas urgentes? Falamos consigo já." },
  ];
  return (
    <section className="bg-[#031411] border-y border-[#1a4640] py-16 sm:py-20" data-testid="benefits-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto">
          <span className="inline-block text-xs uppercase tracking-[0.22em] text-[#d4af37]">
            Porquê <Brand short />
          </span>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl tracking-tight text-[#fdfbf7]">
            O serviço que faz o seu apartamento <span className="italic text-[#d4af37]">brilhar</span>
          </h2>
        </div>
        <div className="mt-10 sm:mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {pillars.map(({ Icon, title, body }) => (
            <article key={title}
                     className="bg-[#062c27] p-6 rounded-2xl border border-[#1a4640] hover:border-[#d4af37]/60 hover:shadow-lg hover:shadow-[#d4af37]/10 hover:-translate-y-0.5 transition-all duration-200"
                     data-testid={`benefit-${title.toLowerCase().replace(/\s+/g, "-")}`}>
              <div className="w-12 h-12 rounded-xl bg-[#d4af37]/15 grid place-items-center text-[#d4af37]">
                <Icon size={22} />
              </div>
              <h3 className="mt-4 font-display text-lg text-[#fdfbf7]">{title}</h3>
              <p className="mt-1.5 text-sm text-[#a0b2af] leading-relaxed">{body}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ────────────────────────────  Pricing teaser  ─────────────────────────
 * Conversion-oriented minimalist teaser. Detailed plan tables live on
 * the dedicated /precos page so the home stays light and focused. The
 * teaser only mentions the three plan names, a persuasive subtitle,
 * and a strong CTA leading to the full pricing page. Clicking any of
 * the plan pills reveals a small info-card with a one-line reference
 * description so the user gets a taste before exploring full pricing.
 * ───────────────────────────────────────────────────────────────────── */
function Pricing() {
  const plans = [
    {
      name: "Essential",
      tagline: "Valor sob consulta",
      desc: "Limpeza profissional para apartamentos e alojamentos locais — preparação completa para o próximo check-in com reposição básica de consumíveis.",
    },
    {
      name: "Professional",
      tagline: "Valor sob consulta",
      desc: "Serviço premium com supervisão da propriedade, registo fotográfico, relatório detalhado e reposição completa. O mais escolhido pelos hosts.",
    },
    {
      name: "Premium Villa",
      tagline: "Orçamento personalizado",
      desc: "Cuidado exclusivo para villas e propriedades de luxo: limpeza detalhada, supervisão completa e serviço personalizado conforme a propriedade.",
    },
  ];
  const [openPlan, setOpenPlan] = useState(null);

  return (
    <section id="precos" className="relative overflow-hidden bg-[#041f1b] py-20 sm:py-28"
             data-testid="pricing-section">
      {/* Subtle radial glow + faint gold gridline for premium texture */}
      <div aria-hidden
           className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(212,175,55,0.10),transparent_60%)]" />
      <div aria-hidden
           className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#d4af37]/30 to-transparent" />

      <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <span className="inline-block text-xs uppercase tracking-[0.22em] text-[#d4af37]"
              data-testid="pricing-teaser-eyebrow">
          Planos &amp; Preços
        </span>
        <h2 className="mt-4 font-display text-3xl sm:text-5xl tracking-tight text-[#fdfbf7]"
            data-testid="pricing-teaser-title">
          O serviço certo para a sua <span className="italic text-[#d4af37]">propriedade</span>
        </h2>
        <p className="mt-5 text-sm sm:text-base text-[#a0b2af] leading-relaxed"
           data-testid="pricing-teaser-subtitle">
          Três planos pensados para alojamentos locais, Airbnb e villas de luxo na Madeira —
          toque num plano para uma referência rápida ou explore os detalhes completos.
        </p>

        {/* Plan name pills — clickable, toggleable. The "Professional" tier
            stays gold-highlighted by default; selecting any pill reveals a
            small info card with a one-line reference description. */}
        <div className="mt-8 flex flex-wrap items-center justify-center gap-2 sm:gap-3"
             data-testid="pricing-teaser-plans">
          {plans.map((p, i) => {
            const isOpen = openPlan === i;
            const featured = i === 1 && openPlan === null;
            return (
              <button key={p.name}
                      type="button"
                      data-testid={`pricing-teaser-plan-${i}`}
                      aria-expanded={isOpen}
                      onClick={() => setOpenPlan(isOpen ? null : i)}
                      className={`px-4 py-2 rounded-full text-xs sm:text-sm tracking-wide border transition-all duration-300 ${
                        isOpen
                          ? "border-[#d4af37] text-[#041f1b] bg-[#d4af37] shadow-[0_0_24px_-6px_rgba(212,175,55,0.85)] -translate-y-0.5"
                          : featured
                            ? "border-[#d4af37] text-[#d4af37] bg-[#d4af37]/5 shadow-[0_0_20px_-8px_rgba(212,175,55,0.6)]"
                            : "border-[#1a4640] text-[#fdfbf7]/80 hover:border-[#d4af37]/50 hover:text-[#d4af37]"
                      }`}>
                {p.name}
              </button>
            );
          })}
        </div>

        {/* Inline info-card revealed when a plan pill is selected */}
        {openPlan !== null && (
          <div data-testid={`pricing-teaser-info-${openPlan}`}
               className="mt-5 mx-auto max-w-md rounded-xl border border-[#d4af37]/40 bg-[#062c27] px-5 py-4 text-left animate-in fade-in slide-in-from-top-2 duration-300">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="font-display text-base text-[#fdfbf7]">{plans[openPlan].name}</div>
                <div className="text-[11px] uppercase tracking-[0.22em] text-[#d4af37] mt-0.5">
                  {plans[openPlan].tagline}
                </div>
              </div>
              <button onClick={() => setOpenPlan(null)}
                      data-testid="pricing-teaser-info-close"
                      aria-label="Fechar"
                      className="shrink-0 text-[#a0b2af] hover:text-[#fdfbf7] transition-colors text-lg leading-none">
                ×
              </button>
            </div>
            <p className="mt-3 text-sm text-[#a0b2af] leading-relaxed">
              {plans[openPlan].desc}
            </p>
            <Link to="/precos"
                  data-testid={`pricing-teaser-info-cta-${openPlan}`}
                  className="mt-3 inline-flex items-center gap-1.5 text-xs uppercase tracking-wider text-[#d4af37] hover:text-[#e8c860] transition-colors">
              Ver detalhes completos <ArrowRight size={12} />
            </Link>
          </div>
        )}

        <div className="mt-10 sm:mt-12 inline-flex flex-col items-center">
          <Link to="/precos"
                data-testid="pricing-teaser-cta"
                className="group inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-md font-semibold uppercase tracking-widest text-xs bg-[#d4af37] text-[#041f1b] hover:bg-[#e8c860] hover:shadow-lg hover:shadow-[#d4af37]/40 hover:-translate-y-0.5 transition-all duration-300">
            Explorar Planos
            <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
          <span className="mt-3 text-[11px] uppercase tracking-[0.22em] text-[#a0b2af]/70">
            Sem compromisso · Orçamento em 24h
          </span>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────  Testimonials  ─────────────────────────── */
function Testimonials({ items }) {
  return (
    <section className="py-16 sm:py-24" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <span className="inline-block text-xs uppercase tracking-[0.22em] text-[#d4af37]">
              O que dizem
            </span>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl tracking-tight text-[#fdfbf7]">
              Clientes que já experimentaram
            </h2>
          </div>
          <div className="flex items-center gap-2 text-[#d4af37]" aria-label="Avaliação 5 estrelas">
            {[...Array(5)].map((_, i) => <Star key={i} size={18} fill="currentColor" stroke="currentColor" />)}
            <span className="text-sm font-semibold text-[#fdfbf7] ml-1">5.0 / Google</span>
          </div>
        </div>
        <div className="mt-10 flex gap-5 overflow-x-auto pb-4 snap-x snap-mandatory"
             data-testid="testimonials-slider">
          {items.map((t, i) => (
            <article key={i}
                     className="snap-center shrink-0 w-[88%] sm:w-[420px] bg-[#062c27] p-7 rounded-2xl border border-[#1a4640] hover:border-[#d4af37]/40 transition-colors"
                     data-testid={`testimonial-${i}`}>
              <div className="flex items-center gap-1 text-[#d4af37] mb-3">
                {[...Array(t.rating)].map((_, j) => <Star key={j} size={14} fill="currentColor" stroke="currentColor" />)}
              </div>
              <p className="text-[#fdfbf7] text-base leading-relaxed">«{t.quote}»</p>
              <div className="mt-5 pt-5 border-t border-[#1a4640] flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#d4af37]/15 text-[#d4af37] grid place-items-center font-bold">
                  {t.name.charAt(0)}
                </div>
                <div>
                  <div className="font-semibold text-[#fdfbf7] text-sm">{t.name}</div>
                  <div className="text-xs text-[#a0b2af]">{t.role}</div>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ──────────────────────────  Trust strip  ─────────────────────────── */
function TrustStrip() {
  const pillars = [
    { Icon: ThumbsUp, title: "Satisfação garantida",
      body: "Não ficou satisfeito? Voltamos sem custo nas 24h seguintes." },
    { Icon: BadgeCheck, title: "Equipa verificada",
      body: "Documentação validada, contratos formais, formação contínua." },
    { Icon: Droplets, title: "Produtos de qualidade",
      body: "Detergentes profissionais, biodegradáveis e seguros para hóspedes." },
  ];
  return (
    <section className="bg-[#031411] border-y border-[#1a4640] py-16 sm:py-20" data-testid="trust-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
        <div className="relative rounded-2xl overflow-hidden border border-[#d4af37]/20 order-2 lg:order-1">
          <img src={TRUST_IMG} alt="Equipa de limpeza profissional"
               loading="lazy" decoding="async"
               className="w-full h-[320px] sm:h-[420px] object-cover" />
        </div>
        <div className="order-1 lg:order-2">
          <span className="inline-block text-xs uppercase tracking-[0.22em] text-[#d4af37]">
            Confiança
          </span>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl tracking-tight text-[#fdfbf7]">
            Trabalhamos para si com <span className="italic text-[#d4af37]">regras claras</span>
          </h2>
          <p className="mt-3 text-[#a0b2af] max-w-lg">
            Anfitriões da Madeira confiam na <Brand short /> para manter os seus padrões hoteleiros.
            Sabe sempre quem entra na sua casa.
          </p>
          <ul className="mt-8 space-y-5">
            {pillars.map(({ Icon, title, body }) => (
              <li key={title} className="flex items-start gap-4"
                  data-testid={`trust-${title.toLowerCase().replace(/\s+/g, "-")}`}>
                <div className="w-11 h-11 rounded-xl bg-[#d4af37]/15 text-[#d4af37] grid place-items-center shrink-0">
                  <Icon size={20} />
                </div>
                <div>
                  <div className="font-display text-lg text-[#fdfbf7]">{title}</div>
                  <div className="text-sm text-[#a0b2af] mt-0.5">{body}</div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

/* ──────────────────  Contact form + Promo card  ──────────────────── */
function ContactPromo() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  function update(p) { setForm(f => ({ ...f, ...p })); }

  async function submit(e) {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) {
      toast.error("Por favor preencha nome, telefone e email.");
      return;
    }
    setSubmitting(true);
    try {
      const userMsg = form.message.trim();
      const baseMsg = "Pedido de orçamento gratuito (homepage).";
      const fullMsg = userMsg
        ? `${baseMsg}\n\nDescrição do cliente:\n${userMsg}`
        : `${baseMsg} Contacto preferido: telefone/email.`;
      await api.post("/contact", {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        message: fullMsg,
      });
      setSubmitted(true);
      toast.success("Pedido enviado! Respondemos em menos de 24h.");
      setForm({ name: "", phone: "", email: "", message: "" });
    } catch (err) {
      toast.error("Não foi possível enviar — tente novamente ou ligue-nos.");
    } finally { setSubmitting(false); }
  }

  return (
    <section id="contacto" className="py-16 sm:py-24" data-testid="contact-section">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Form */}
        <div className="bg-[#062c27] p-6 sm:p-10 rounded-3xl border border-[#1a4640] shadow-2xl shadow-black/30">
          <span className="inline-block text-xs uppercase tracking-[0.22em] text-[#d4af37]">
            Orçamento gratuito · 24h
          </span>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl tracking-tight text-[#fdfbf7]">
            Receba o seu orçamento <span className="italic text-[#d4af37]">sem compromisso</span>
          </h2>
          <p className="mt-2 text-[#a0b2af]">
            Deixe os contactos e respondemos em menos de 24h com uma proposta personalizada.
          </p>
          {submitted ? (
            <div className="mt-6 p-6 rounded-2xl bg-[#d4af37]/10 border border-[#d4af37]/40 text-[#fdfbf7] flex items-start gap-3"
                 data-testid="contact-form-success">
              <CheckCircle2 size={22} className="text-[#d4af37] shrink-0 mt-0.5" />
              <div>
                <div className="font-display text-lg">Pedido recebido!</div>
                <div className="text-sm mt-1 text-[#a0b2af]">A nossa equipa entrará em contacto consigo em breve.</div>
              </div>
            </div>
          ) : (
            <form className="mt-6 space-y-4" onSubmit={submit} data-testid="contact-form">
              <Field label="Nome" required value={form.name}
                     onChange={v => update({ name: v })}
                     placeholder="O seu nome"
                     testId="contact-form-name" />
              <Field label="Telefone" required value={form.phone}
                     onChange={v => update({ phone: v })}
                     placeholder="+351 ..."
                     type="tel" testId="contact-form-phone" />
              <Field label="Email" required value={form.email}
                     onChange={v => update({ email: v })}
                     placeholder="seu@email.pt"
                     type="email" testId="contact-form-email" />
              <label className="block">
                <span className="text-xs font-semibold text-[#a0b2af] uppercase tracking-widest">
                  Descreva o serviço que precisa <span className="lowercase tracking-normal text-[#a0b2af]/70">(opcional)</span>
                </span>
                <textarea
                  value={form.message}
                  onChange={e => update({ message: e.target.value })}
                  placeholder="Ex: T2 em Funchal com 2 WC, limpeza após check-out aos sábados, ~6 reservas/mês…"
                  rows={4}
                  maxLength={1000}
                  className="mt-1.5 w-full px-4 py-3 rounded-sm border border-[#1a4640] bg-[#031411] text-[#fdfbf7] placeholder-[#a0b2af]/60 focus:border-[#d4af37] focus:outline-none focus:ring-2 focus:ring-[#d4af37]/40 transition-colors resize-y min-h-[110px]"
                  data-testid="contact-form-message"
                />
                <span className="block text-[10px] text-[#a0b2af]/70 mt-1 text-right">
                  {form.message.length}/1000
                </span>
              </label>
              <button type="submit" disabled={submitting}
                      className="w-full inline-flex justify-center items-center gap-2 px-7 py-4 text-sm uppercase tracking-widest font-semibold rounded-sm text-[#031411] bg-[#d4af37] hover:bg-[#f5d76e] shadow-lg shadow-[#d4af37]/20 transition-all duration-200 disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-[#d4af37] focus:ring-offset-2 focus:ring-offset-[#062c27]"
                      data-testid="contact-form-submit">
                {submitting
                  ? <><Loader2 size={18} className="animate-spin" /> A enviar…</>
                  : <>Receber orçamento gratuito <ArrowRight size={18} /></>}
              </button>
              <p className="text-xs text-[#a0b2af] text-center">
                Ao submeter aceita a nossa <Link to="/privacidade" className="underline hover:text-[#d4af37]">Política de Privacidade</Link>.
              </p>
            </form>
          )}

          <div className="mt-6 pt-6 border-t border-[#1a4640] grid sm:grid-cols-2 gap-3">
            <a href={PHONE_LINK}
               className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-sm border-2 border-[#d4af37]/40 text-[#d4af37] font-semibold uppercase tracking-widest text-xs hover:bg-[#d4af37]/10 focus:outline-none focus:ring-2 focus:ring-[#d4af37] transition-colors"
               data-testid="contact-phone-btn">
              <Phone size={16} /> Ligar agora
            </a>
            <a href={`https://wa.me/351967386076?text=Olá%20${encodeURIComponent(brandShort())}!%20Gostaria%20de%20um%20orçamento.`}
               target="_blank" rel="noopener noreferrer"
               className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-sm text-white bg-[#25D366] hover:bg-[#1ebe5a] font-semibold uppercase tracking-widest text-xs shadow-md shadow-[#25D366]/20 focus:outline-none focus:ring-2 focus:ring-[#25D366]/60 transition-colors"
               data-testid="contact-whatsapp-btn">
              <MessageCircle size={16} fill="currentColor" /> WhatsApp
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ──────────────────────────  Helpers  ─────────────────────────────── */
function Field({ label, value, onChange, placeholder, type = "text", required, testId }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold text-[#a0b2af] uppercase tracking-widest">
        {label}{required ? " *" : ""}
      </span>
      <input
        type={type} value={value} onChange={e => onChange(e.target.value)}
        placeholder={placeholder} required={required}
        className="mt-1.5 w-full px-4 py-3 rounded-sm border border-[#1a4640] bg-[#031411] text-[#fdfbf7] placeholder-[#a0b2af]/60 focus:border-[#d4af37] focus:outline-none focus:ring-2 focus:ring-[#d4af37]/40 transition-colors"
        data-testid={testId}
      />
    </label>
  );
}

function StickyMobileCTA() {
  return (
    <div className="sm:hidden fixed bottom-3 left-3 right-3 z-50 animate-fadeIn"
         data-testid="sticky-mobile-cta">
      <a href="#contacto"
         onClick={scrollToContacto}
         className="block w-full px-6 py-4 rounded-sm text-center font-semibold uppercase tracking-widest text-sm text-[#031411] bg-[#d4af37] hover:bg-[#f5d76e] shadow-2xl shadow-black/40 focus:outline-none focus:ring-2 focus:ring-[#d4af37]"
         data-testid="sticky-mobile-cta-link">
        Pedir orçamento gratuito
      </a>
    </div>
  );
}
